import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NgbTabset } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { forkJoin, Subscription } from 'rxjs';
import { finalize, map, take, tap } from 'rxjs/operators';
import { utils, WorkBook, WorkSheet, writeFile } from 'xlsx';

import { AuthService } from '../../auth/auth.service';
import { Util } from '../../common/common.util';
import { ProfileUserService } from '../../user/profile.user.service';
import { TranslationKeyMap } from '../reports.i18n';
import { ReportsService } from '../reports.service';
import { toCamel } from '../td-daily-huddle/td-daily-huddle.reports.component';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Role } from './../../common/common.service';
import {
    TdBuilderUsageIndividualReportPopulationType,
    TdBuilderUsageIndividualReportResultParams,
    TdBuilderUsageIndividualReportResultRow,
    TdBuilderUsageIndividualReportSelectionParams,
    TdBuilderUsageIndividualResult,
} from './td-builder-usage-individual.reports.model';

enum Tabs {
    PARAMS = 'w-tab-report-0',
    GRID = 'w-tab-report-1',
}

@Component({
    selector: '[tdBuilderUsageIndividualReport]',
    templateUrl: './td-builder-usage-individual.reports.component.html',
    host: {
        class: 'cmps-reports-td-builder-usage-individual',
    },
})
export class TdBuilderUsageIndividualReportComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild('tabSet', { static: true }) tabSet: NgbTabset;
    @ViewChild('table') table: ElementRef<HTMLTableElement>;
    i18n = TranslationKeyMap;
    pendingData: boolean;
    noData: boolean = false;
    reportId: number;
    currentDate = new Date();
    expandedRows = Array<boolean>();
    selectedParams: TdBuilderUsageIndividualReportSelectionParams = {
        startDate: Util.dateToNgbDate(
            new Date(
                this.currentDate.getFullYear(),
                this.currentDate.getMonth(),
                1,
            ),
        ),
        endDate: Util.dateToNgbDate(this.currentDate),
        populationType: TdBuilderUsageIndividualReportPopulationType.Individual,
        populationID: null,
        defaultPopulationID: null,
        invalidStartDate: false,
        invalidEndDate: false,
        pendingData: false,
        noData: false,
    };

    groups = Array<TdBuilderUsageIndividualReportResultRow>();
    params: TdBuilderUsageIndividualReportResultParams;

    sub: Subscription;
    selectedPopulation: string;
    selectedPopulationType: string;
    roles: IOption[];
    peerGroups: IOption[];
    superGroups: IOption[];
    reportPopulations: IOption[] = [
        {
            label: 'Role',
            value: TdBuilderUsageIndividualReportPopulationType.Role,
        },
        {
            label: 'SuperGroup',
            value: TdBuilderUsageIndividualReportPopulationType.SuperGroup,
        },
        {
            label: 'PeerGroup',
            value: TdBuilderUsageIndividualReportPopulationType.PeerGroup,
        },
        {
            label: 'Individual',
            value: TdBuilderUsageIndividualReportPopulationType.Individual,
        },
        {
            label: 'Reps Under',
            value: TdBuilderUsageIndividualReportPopulationType.ReptsUnder,
        },
    ];

    generatedTimesLabel = 'Generation Times';

    constructor(
        private readonly _reportService: ReportsService,
        private readonly _profileUserService: ProfileUserService,
        private readonly _authService: AuthService,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.selectedParams.pendingData = true;
        const user = {
            userId: this._authService.user.id,
            userName: `${this._authService.user.firstName} ${this._authService.user.lastName}`,
        };
        this.selectedParams = {
            ...this.selectedParams,
            populationID: user,
            defaultPopulationID: user,
        };
        forkJoin([
            this._profileUserService.listRoles().pipe(
                take(1),
                map((roles) =>
                    roles.map((p) => ({
                        value: p.id.toString(),
                        label: p.name,
                    })),
                ),
                // eslint-disable-next-line no-return-assign
                tap((roles) => (this.roles = roles)),
            ),
            this._profileUserService.listPeerGroups().pipe(
                take(1),
                map((peerGroups) =>
                    peerGroups
                        .filter((p) => p.enabled)
                        .map((p) => ({
                            value: p.id.toString(),
                            label: p.name,
                        })),
                ),
                // eslint-disable-next-line no-return-assign
                tap((peerGroups) => (this.peerGroups = peerGroups)),
            ),
            this._profileUserService.listSuperGroups().pipe(
                take(1),
                map((superGroups) =>
                    superGroups
                        .filter((s) => s.enabled)
                        .map((s) => ({
                            value: s.id.toString(),
                            label: s.name,
                        })),
                ),
                // eslint-disable-next-line no-return-assign
                tap((superGroups) => (this.superGroups = superGroups)),
            ),
        ])
            .pipe(
                finalize(() => {
                    this.selectedParams.pendingData = false;
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterViewInit() {
        this.sub = this.tabSet.tabChange
            .pipe(
                tap((t) => {
                    if (t.nextId === Tabs.GRID) {
                        this.getReportResult();
                    }
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getReportResult() {
        if (!this.reportId) {
            this.noData = true;
            return;
        }
        this.pendingData = true;
        this._reportService
            .retrieveGenericReportResult(this.reportId)
            .pipe(
                take(1),
                tap((result) => {
                    if (result.resultSet && result.resultSet.length > 0) {
                        this.params = this.getParams(result);
                        this.getSelectedPopulation();
                        const data =
                            toCamel<TdBuilderUsageIndividualReportResultRow>(
                                JSON.parse(result.resultSet),
                            );
                        if (Array.isArray(data)) {
                            this.groups = this.processData(data);
                            this.groups.forEach((element) => {
                                this.expandedRows.push(false);
                            });
                        }
                    } else {
                        this.noData = true;
                    }
                }),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this.pendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGeneratedReport(reportId: number) {
        this.reportId = reportId;
        this.getReportResult();
        if (this.expandedRows.length > 0) {
            this.expandedRows = this.contractRows();
        }
        this.tabSet.select('w-tab-report-1');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onExportClick() {
        this.expandedRows = this.contractRows();
        const generated =
            this.table.nativeElement.querySelectorAll('.generatedRow');
        if (generated.length > 0) {
            generated[0].remove();
        }
        const workSheet: WorkSheet = utils.table_to_sheet(
            this.table.nativeElement,
        );
        const workBook: WorkBook = utils.book_new();
        utils.book_append_sheet(workBook, workSheet, 'Sheet1');
        writeFile(workBook, 'Report Builder Usage - Metric.xlsx', {
            bookType: 'xlsx',
            type: 'buffer',
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private getSelectedPopulation() {
        const selected = this.reportPopulations.find(
            (p) =>
                p.label.split(' ').join('') === this.params.populationType ||
                p.value === this.params.populationType,
        );

        this.selectedPopulationType = selected.label;

        switch (selected.value) {
            case TdBuilderUsageIndividualReportPopulationType.Role:
                // eslint-disable-next-line no-case-declarations
                const role = this.roles.find(
                    (s) => s.value === this.params.populationID,
                );
                this.selectedPopulation = role ? role.label : '';
                break;
            case TdBuilderUsageIndividualReportPopulationType.SuperGroup:
                // eslint-disable-next-line no-case-declarations
                const superGroup = this.superGroups.find(
                    (s) => s.value === this.params.populationID,
                );
                this.selectedPopulation = superGroup ? superGroup.label : '';
                break;
            case TdBuilderUsageIndividualReportPopulationType.PeerGroup:
                // eslint-disable-next-line no-case-declarations
                const peerGroup = this.peerGroups.find(
                    (s) => s.value === this.params.populationID,
                );
                this.selectedPopulation = peerGroup ? peerGroup.label : '';
                break;
            case TdBuilderUsageIndividualReportPopulationType.Individual:
            case TdBuilderUsageIndividualReportPopulationType.ReptsUnder:
                this._reportService
                    .retrieveUsersNameById(this.params.populationID)
                    .pipe(
                        take(1),
                        tap((r) => {
                            if (r.length > 0) {
                                this.selectedPopulation = r[0].userName;
                            }
                        }),
                    )
                    .subscribe();
                break;
            default:
                break;
        }
    }

    processData(
        data: TdBuilderUsageIndividualReportResultRow[],
    ): TdBuilderUsageIndividualReportResultRow[] {
        if (
            this.params.populationType ===
            TdBuilderUsageIndividualReportPopulationType.ReptsUnder
        ) {
            data = data.filter(
                (el: TdBuilderUsageIndividualReportResultRow) => {
                    return el.role === 'Agent';
                },
            );
            if (data.length === 0) {
                this.noData = true;
                return [];
            }
        }

        data.forEach((el) => {
            if (el.role === '') {
                el.role = '-';
            }

            if (el.manager === '') {
                el.manager = '-';
            }

            if (el.superGroup === '') {
                el.superGroup = '-';
            }

            if (el.dateList === '') {
                el.dateList = [];
            } else {
                const arrayDate = new Array<Date>();
                const dList = JSON.parse(el.dateList);
                if (Array.isArray(dList)) {
                    dList.forEach((element) => {
                        arrayDate.push(new Date(element.ReportDate));
                    });
                    el.dateList = arrayDate;
                }
            }
        });
        this.noData = data.length === 0;
        return data;
    }

    private getParams(
        result: TdBuilderUsageIndividualResult,
    ): TdBuilderUsageIndividualReportResultParams {
        return result.params.split('@').reduce((output, current) => {
            const keyValue = current.split('=').map((v) => v.trim());
            const key = (
                keyValue[0].charAt(0).toLowerCase() + keyValue[0].slice(1) ||
                keyValue[0]
            ).toString();
            const value = keyValue[1];
            if (key) {
                output[key] = value.replace(/^,+|,+$/g, '');
            }
            return output;
        }, {}) as TdBuilderUsageIndividualReportResultParams;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    expandRow(i: number) {
        if (this.expandedRows[i]) {
            this.expandedRows[i] = false;
        } else {
            this.expandedRows = this.expandedRows.map((element, index) => {
                // eslint-disable-next-line no-return-assign
                return (element = index === i);
            });
        }
    }

    contractRows(): boolean[] {
        const contractedRows: boolean[] = new Array<boolean>();
        this.expandedRows.forEach((element) => {
            contractedRows.push(false);
        });
        return contractedRows;
    }
}
